import React, { ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { UiComponent } from 'components';
import { tableWrap, tableToolbar } from 'styles/table';
import { IPageOption, IPushMessage } from 'types';
import { BsPlusLg } from 'react-icons/bs';
import dayjs from 'dayjs';
import { NOTI_MENU_URL } from 'data/menu';
import {
  PUSH_DESTINATION_LABEL,
  PUSH_STATUS_LABEL,
  PUSH_TYPE_LABEL,
} from '../constants';

const COLS: string[] = [
  'ID',
  '상태',
  '유형',
  '제목',
  '연결 화면',
  '예약 일시',
  '발송 일시',
];

interface Props {
  pageOption: IPageOption;
  handlePage: (option: IPageOption) => void;
  data: IPushMessage[];
  totalPage: number;
  toolbar?: ReactNode;
}

const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-';

function List({ data, toolbar, ...pageOptions }: Props) {
  const navigate = useNavigate();
  const pushUrl = NOTI_MENU_URL['앱 푸시 관리'];

  const renderer: Record<
    (typeof COLS)[number],
    (data: IPushMessage & { row: number }) => ReactNode
  > = useMemo(
    () => ({
      ID: ({ id }: IPushMessage) => <>{id}</>,
      상태: ({ status }: IPushMessage) => <>{PUSH_STATUS_LABEL[status]}</>,
      유형: ({ pushType }: IPushMessage) => <>{PUSH_TYPE_LABEL[pushType]}</>,
      제목: ({ title }: IPushMessage) => (
        <p css={{ textAlign: 'left' }}>{title}</p>
      ),
      '연결 화면': ({ destinationType, destinationDetail }: IPushMessage) => (
        <>
          {PUSH_DESTINATION_LABEL[destinationType]}
          {destinationDetail ? ` (#${destinationDetail})` : ''}
        </>
      ),
      '예약 일시': ({ sendAt }: IPushMessage) => <>{formatDateTime(sendAt)}</>,
      '발송 일시': ({ sentAt }: IPushMessage) => <>{formatDateTime(sentAt)}</>,
    }),
    [],
  );

  return (
    <UiComponent.Box>
      <div css={tableToolbar}>
        {toolbar ?? <div />}
        <div css={{ marginLeft: 'auto' }}>
          <UiComponent.Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`${pushUrl}/register`)}
            css={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '0 4px',
            }}
          >
            <BsPlusLg size={15} />
            <p css={{ fontSize: '14px' }}>푸시 작성</p>
          </UiComponent.Button>
        </div>
      </div>
      <div css={tableWrap}>
        <UiComponent.Table
          cols={COLS}
          renderer={renderer}
          data={data}
          sticky
          onClickRow={({ id }: IPushMessage) => navigate(`${pushUrl}/${id}`)}
        />
      </div>
      <UiComponent.Pagination {...pageOptions} />
    </UiComponent.Box>
  );
}

export default List;
